import type { ComponentPropsWithoutRef, ElementRef } from "react";
import { forwardRef } from "react";

import { tw } from "@/shared/utils";

const Root = forwardRef<ElementRef<"div">, ComponentPropsWithoutRef<"div">>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={tw(
        "relative z-10 flex grow overflow-hidden rounded-lg bg-white shadow-sm",
        className,
      )}
      {...props}
    />
  ),
);
Root.displayName = "CreditCard.Root";

const Content = forwardRef<ElementRef<"div">, ComponentPropsWithoutRef<"div">>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={tw("flex grow flex-col justify-center gap-4 p-3", className)}
      {...props}
    />
  ),
);
Content.displayName = "CreditCard.Content";

const Title = forwardRef<ElementRef<"h3">, ComponentPropsWithoutRef<"h3">>(
  ({ className, children, ...props }, ref) => (
    <h3
      ref={ref}
      className={tw("text-2xl font-medium text-neutral-950", className)}
      {...props}
    >
      {children}
    </h3>
  ),
);
Title.displayName = "CreditCard.Title";

const Description = forwardRef<ElementRef<"p">, ComponentPropsWithoutRef<"p">>(
  ({ className, ...props }, ref) => (
    <p
      ref={ref}
      className={tw("text-xs text-neutral-800", className)}
      {...props}
    />
  ),
);
Description.displayName = "CreditCard.Description";

const Footer = forwardRef<ElementRef<"div">, ComponentPropsWithoutRef<"div">>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={tw("flex items-center", className)} {...props} />
  ),
);
Footer.displayName = "CreditCard.Footer";

const Background = forwardRef<
  ElementRef<"div">,
  ComponentPropsWithoutRef<"div">
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={tw(
      "pointer-events-none absolute right-0 -z-10 flex h-full w-1/5 flex-1 overflow-hidden rounded-r-lg bg-gradient-to-b from-secondary-100 to-primary-100 sm:w-28 sm:flex-none",
      className,
    )}
    {...props}
  />
));
Background.displayName = "CreditCard.Background";

const Chip = forwardRef<ElementRef<"div">, ComponentPropsWithoutRef<"div">>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={tw(
        "absolute top-4 left-4 h-5 w-7 rounded-md bg-yellow-500",
        className,
      )}
      {...props}
    />
  ),
);
Chip.displayName = "CreditCard.Chip";

export const CreditCard = {
  Root,
  Footer,
  Title,
  Description,
  Content,
  Chip,
  Background,
};
